import { Add, Tune } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
  debounce,
  styled,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { useGetArnoTopObjects } from '../hooks/reactQuerys/ARNOtopQueries';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';
import {
  useGetLegalTransactionRecordNumbers,
  useGetLegalTransactionTypes,
  useSearchLegalTransactions,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';
import { ISearchOption } from '../Interfaces/ISearchOption';
import { SearchType } from '../Interfaces/ISearchType';
import { LegalTransactionEdit } from './LegalTransactionEdit';
import { LegalTransactionNew } from './LegalTransactionNew';

type FormType = 'edit' | 'new';

const StyledDataGrid = styled(DataGrid)({
  '& .highlighted-row': {
    backgroundColor: '#e8f1e4 !important', // Helles Grün
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#c8e0c2 !important', // Dunkleres Grün beim Hover
    },
  },
  '& .normal-row': {
    cursor: 'pointer',
  },
});

export const columns: GridColDef[] = [
  { field: 'idLegalTransaction', headerName: 'Nr.', width: 90 },
  { field: 'LegalTransactionType', headerName: 'Art', width: 200 },
  { field: 'LegalTransactionProgress', headerName: 'Phase', width: 150 },
  { field: 'LegalTransactionState', headerName: 'Status', width: 150 },
  { field: 'Title', headerName: 'Bezeichnung', flex: 1 },
];

const possibleSearchOptions: ISearchOption[] = [
  { SearchOptionName: 'Titel', SearchOptionKey: 'Title' },
  {
    SearchOptionName: 'Vorgangsnummer',
    SearchOptionKey: 'TransactionNumberEntry',
  },
  { SearchOptionName: 'Urkundennummer', SearchOptionKey: 'RecordNumberEntry' },
];

const initialRowsPerPage = 25;

const LoadingOverlay = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(1px)',
        zIndex: 1,
        height: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

export const LegalTransactionOverview = () => {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();

  const [idLegalTransaction, setIdLegalTransaction] = useState<number | null>(
    params.idLegalTransaction ? Number(params.idLegalTransaction) : null
  );

  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialRowsPerPage);
  const [searchType, setSearchType] = useState(SearchType.INCLUDES);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchOptions, setSearchOptions] = useState(possibleSearchOptions);

  let searchQuery = '';

  for (let i = 0; i < searchOptions.length; i++) {
    searchQuery += `${searchOptions[i].SearchOptionKey}:${debouncedSearchTerm};`;
  }

  const searchUri =
    debouncedSearchTerm === ''
      ? `page=${page}&rowsPerPage=${pageSize}`
      : `search=${searchQuery}&searchtype=${searchType}&page=${page}&rowsPerPage=${pageSize}`;

  const { legalTransactionRecordNumbers } =
    useGetLegalTransactionRecordNumbers();

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    abortSearchLegalTransactions,
  } = useSearchLegalTransactions(searchUri);

  const { users, isLoadingUsers } = useGetUsers();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();

  const { legalClients, isLoadingLegalClients } = useGetLegalClients();

  const { arnoTopObjects } = useGetArnoTopObjects();

  const [showCreateNewDialog, setShowCreateNewDialog] = useState(false);

  const textFieldRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIdLegalTransaction(
      params.idLegalTransaction ? Number(params.idLegalTransaction) : null
    );
  }, [params.idLegalTransaction]);

  const handleClose = async (isEdited: boolean, type: FormType) => {
    if (!isEdited) {
      if (type === 'edit') {
        navigate('/legaltransaction');
      } else {
        setShowCreateNewDialog(false);
      }

      return;
    }

    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Es gibt unbearbeitete Änderungen in diesem Vorgang. Sind Sie sicher, dass Sie den Vorgang ohne Speichern schließen wollen?',
      'Schließen ohne Speichern',
      'Abbrechen',
      theme
    );

    if (isConfirm) {
      if (type === 'edit') {
        navigate('/legaltransaction');
      } else {
        setShowCreateNewDialog(false);
      }
    }
  };

  const handleLegalTransactionFinished = (idLegalTransaction: number) => {
    navigate(`/legaltransaction/${idLegalTransaction}`);
    setIdLegalTransaction(idLegalTransaction);
    setShowCreateNewDialog(false);
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setSearchTerm(searchTerm);

    const debouncedSearchTerm = debounce(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    debouncedSearchTerm();
  };

  // Neue Spalte, die du vor 'Title' hinzufügen möchtest
  const newColumn = {
    field: 'RecordNumbers',
    headerName: 'Urkundennummer',
    width: 200,
    valueGetter: ({ row }: GridValueGetterParams) =>
      legalTransactionRecordNumbers === undefined
        ? ''
        : legalTransactionRecordNumbers
            .filter(
              x => x.idLegalTransaction === Number(row.idLegalTransaction)
            )
            .map(x => x.RecordNumberEntry)
            .join(', '),
  };

  // Neuer Array, wobei du das neue Element vor 'Title' einfügst
  const newColumns = columns
    .map((col, index) => {
      // Füge die neue Spalte vor der Spalte 'Title' ein
      if (col.field === 'Title') {
        return [newColumn, col]; // Das neue Element wird vor dem aktuellen 'Title' eingefügt
      }
      return col; // Für alle anderen Spalten bleib unverändert
    })
    .flat(); // Die Array von Arrays wieder zu einem einzelnen Array zusammenfassen

  if (
    isLoadingUsers ||
    isLoadingUserGroups ||
    isLoadingLegalTransactionTypes ||
    isLoadingLegalClients
  ) {
    return <LoadingSpinner />;
  }

  if (
    !users ||
    !userGroups ||
    !legalTransactionTypes ||
    !legalClients ||
    (!isLoadingLegalTransactions && !legalTransactions)
  ) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography>
          Ein Fehler ist aufgetreten! Bitte versuchen Sie es später erneut.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ m: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='h4'>Vorgänge</Typography>
          <IconButton onClick={() => setShowCreateNewDialog(true)}>
            <Add />
          </IconButton>
        </Box>

        <Grid container mt={1} spacing={1}>
          <Grid item sm={2}>
            <TextField
              sx={{ mt: 2, mb: 2 }}
              label='Suchbedingung'
              value={searchType}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                abortSearchLegalTransactions();

                setSearchType(event.target.value as SearchType);
                setPage(1);
              }}
              fullWidth
              select
            >
              <MenuItem value={SearchType.STARTS_WITH}>
                Beginnt mit Sucheingabe
              </MenuItem>
              <MenuItem value={SearchType.INCLUDES}>
                Enthält Sucheingabe
              </MenuItem>
              <MenuItem value={SearchType.EXACT_MATCH}>
                Identisch mit Sucheingabe
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item sm={10}>
            <TextField
              ref={textFieldRef}
              sx={{ mt: 2, mb: 2 }}
              label='Suche'
              fullWidth
              value={searchTerm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value !== searchTerm) {
                  abortSearchLegalTransactions();
                  handleSearchTermChange(event.target.value);
                  setPage(1);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() => setShowSearchOptions(prev => !prev)}
                    >
                      <Tune />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Popover
            id={'filter'}
            open={showSearchOptions}
            anchorEl={textFieldRef.current}
            onClose={() => {
              setShowSearchOptions(false);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Autocomplete
              multiple
              options={possibleSearchOptions}
              getOptionLabel={option => option.SearchOptionName}
              value={searchOptions}
              onChange={(_, newValue) => {
                setSearchOptions(newValue);
                setPage(1);
              }}
              renderTags={(value: ISearchOption[], getTagProps) =>
                value.map((option: ISearchOption, index: number) => (
                  <Chip
                    label={option.SearchOptionName}
                    {...getTagProps({ index })}
                  />
                ))
              }
              style={{ padding: 10, width: textFieldRef?.current?.clientWidth }}
              isOptionEqualToValue={(
                option: ISearchOption,
                value: ISearchOption
              ) => {
                return option.SearchOptionKey === value.SearchOptionKey;
              }}
              renderInput={params => (
                <TextField {...params} variant='outlined' label='Suche nach' />
              )}
            />
          </Popover>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={12}>
            <StyledDataGrid
              loading={isLoadingLegalTransactions}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={legalTransactions ?? []}
              columns={newColumns}
              getRowId={row => row.idLegalTransaction}
              onRowClick={params =>
                navigate(`/legaltransaction/${params.row.idLegalTransaction}`)
              }
              autoHeight
              pagination
              paginationModel={{ page, pageSize }}
              rowCount={legalTransactions?.length ?? 0}
              onPaginationModelChange={model => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              paginationMode='server'
              pageSizeOptions={[initialRowsPerPage, 50, 100]}
              slots={{
                loadingOverlay: () => <LoadingOverlay />,
              }}
              getRowClassName={params =>
                params.row.idLegalTransactionState === 1
                  ? 'highlighted-row'
                  : 'normal-row'
              }
            />
          </Grid>
          <Grid
            item
            sm={12}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          ></Grid>
        </Grid>
      </Box>

      {idLegalTransaction && (
        <LegalTransactionEdit
          key={`idLegalTransaction-${idLegalTransaction}`}
          idLegalTransaction={idLegalTransaction}
          handleClose={isEdited => handleClose(isEdited, 'edit')}
          users={users}
          userGroups={userGroups}
          legalTransactionTypes={legalTransactionTypes}
          legalClients={legalClients}
          arnoTopObjects={arnoTopObjects}
        />
      )}

      {showCreateNewDialog && (
        <LegalTransactionNew
          users={users}
          userGroups={userGroups}
          legalTransactionTypes={legalTransactionTypes}
          handleClose={isEdited => handleClose(isEdited, 'new')}
          handleFinished={handleLegalTransactionFinished}
        />
      )}
    </>
  );
};
